import { ArrowLeft, User, Trophy, TrendingUp, Activity, ArrowUp, ArrowDown } from 'lucide-react';
import { useUserStats } from '../hooks/useLeaderboardData';
import { StatsCard } from '../components/StatsCard';
import { RankBadge } from '../components/RankBadge';
import { formatCurrency, formatNumber } from '../utils/formatters';

interface UserProfileProps {
  address: string;
}

export function UserProfile({ address }: UserProfileProps) {
  const { data: userStats, loading, error } = useUserStats(address || null);

  const handleBack = () => {
    window.history.back();
  };

  const formatAddress = (addr: string) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-white/10 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-white/10 rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-white/10 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !userStats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-300 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="glass-card p-8 text-center">
            <User className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">User Not Found</h2>
            <p className="text-slate-300">
              {error || 'This user address was not found in the leaderboard.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-slate-300 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* User Header */}
        <div className="glass-card p-8 mb-8 animate-fade-in">
          <div className="flex items-center gap-6 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">User Profile</h1>
              <div className="font-mono text-lg text-slate-300">
                {userStats.displayAddress}
              </div>
              <div className="text-sm text-slate-400 mt-1">
                Full Address: {userStats.address}
              </div>
            </div>
            {userStats.overallRank && (
              <div className="ml-auto">
                <RankBadge rank={userStats.overallRank} size="lg" />
              </div>
            )}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Overall Rank"
            value={userStats.overallRank ? `#${userStats.overallRank}` : 'Unranked'}
            icon={<Trophy className="w-6 h-6" />}
            gradient="gradient-primary"
            loading={false}
          />
          <StatsCard
            title="Total Volume"
            value={formatCurrency(parseFloat(userStats.totalVolume))}
            icon={<TrendingUp className="w-6 h-6" />}
            gradient="gradient-secondary"
            loading={false}
          />
          <StatsCard
            title="Total Transactions"
            value={formatNumber(userStats.totalTransactions)}
            icon={<Activity className="w-6 h-6" />}
            gradient="gradient-accent"
            loading={false}
          />
          <StatsCard
            title="Avg Transaction"
            value={formatCurrency(parseFloat(userStats.averageTransactionSize))}
            icon={<TrendingUp className="w-6 h-6" />}
            gradient="gradient-success"
            loading={false}
          />
        </div>

        {/* Token Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="glass-card p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <img 
                src="/images/usdc.png" 
                alt="USDC" 
                className="w-8 h-8 rounded-full"
              />
              USDC Volume
            </h3>
            <div className="text-3xl font-bold text-blue-400 mb-2">
              {formatCurrency(parseFloat(userStats.usdcVolume))}
            </div>
            <div className="text-sm text-slate-400">
              {userStats.totalVolume !== '0' 
                ? `${((parseFloat(userStats.usdcVolume) / parseFloat(userStats.totalVolume)) * 100).toFixed(1)}% of total volume`
                : '0% of total volume'
              }
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <img 
                src="/images/usdt.png" 
                alt="USDT" 
                className="w-8 h-8 rounded-full"
              />
              USDT Volume
            </h3>
            <div className="text-3xl font-bold text-green-400 mb-2">
              {formatCurrency(parseFloat(userStats.usdtVolume))}
            </div>
            <div className="text-sm text-slate-400">
              {userStats.totalVolume !== '0' 
                ? `${((parseFloat(userStats.usdtVolume) / parseFloat(userStats.totalVolume)) * 100).toFixed(1)}% of total volume`
                : '0% of total volume'
              }
            </div>
          </div>
        </div>

        {/* Bridge Direction Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="glass-card p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <ArrowUp className="w-6 h-6 text-green-400" />
              To Shido
            </h3>
            <div className="text-3xl font-bold text-green-400 mb-2">
              {formatCurrency(parseFloat(userStats.toShidoVolume))}
            </div>
            <div className="text-sm text-slate-400">
              Bridging tokens to Shido Network
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <ArrowDown className="w-6 h-6 text-red-400" />
              From Shido
            </h3>
            <div className="text-3xl font-bold text-red-400 mb-2">
              {formatCurrency(parseFloat(userStats.fromShidoVolume))}
            </div>
            <div className="text-sm text-slate-400">
              Bridging tokens from Shido Network
            </div>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="glass-card p-8 mb-8">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-400" />
            Detailed Activity Breakdown
          </h3>
          
          {userStats.breakdown && userStats.breakdown.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {userStats.breakdown.map((detail, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img 
                        src={`/images/${detail.token.toLowerCase()}.png`} 
                        alt={detail.token} 
                        className="w-6 h-6 rounded-full"
                      />
                      <div className="flex items-center gap-2">
                        {detail.bridgeDirection === 'to_shido' ? (
                          <ArrowUp className="w-4 h-4 text-green-400" />
                        ) : (
                          <ArrowDown className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-white font-medium">
                          {detail.bridgeDirection === 'to_shido' ? 'To Shido' : 'From Shido'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Volume:</span>
                      <span className="text-white font-bold">{formatCurrency(parseFloat(detail.volume))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Transactions:</span>
                      <span className="text-white">{formatNumber(detail.transactionCount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">First Transaction:</span>
                      <span className="text-white text-sm">{new Date(detail.firstTransaction).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Last Transaction:</span>
                      <span className="text-white text-sm">{new Date(detail.lastTransaction).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No detailed breakdown available
            </div>
          )}
        </div>

        {/* Activity Timeline */}
        <div className="glass-card p-8">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Activity className="w-6 h-6 text-purple-400" />
            Activity Timeline
          </h3>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg">
              <div>
                <div className="text-white font-medium">First Bridge Transaction</div>
                <div className="text-slate-400 text-sm">Started bridging activity</div>
              </div>
              <div className="text-right">
                <div className="text-white">{new Date(userStats.firstTransaction).toLocaleDateString()}</div>
                <div className="text-slate-400 text-sm">{new Date(userStats.firstTransaction).toLocaleTimeString()}</div>
              </div>
            </div>
            
            <div className="flex justify-between items-center p-4 bg-white/5 rounded-lg">
              <div>
                <div className="text-white font-medium">Latest Bridge Transaction</div>
                <div className="text-slate-400 text-sm">Most recent activity</div>
              </div>
              <div className="text-right">
                <div className="text-white">{new Date(userStats.lastTransaction).toLocaleDateString()}</div>
                <div className="text-slate-400 text-sm">{new Date(userStats.lastTransaction).toLocaleTimeString()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
